/**
 * 모빌리티 상품의 차량 제원(`products.specifications`) → 화면 행 목록.
 *
 * 값 포맷은 전부 이 순수 함수에 모아 둔다(컴포넌트는 그리기만 한다):
 * - 클래스 라벨은 i18n 메시지가 아니라 `bike-classes.ts` 상수 (같은 파일 §주석 참고)
 * - 나머지 라벨/값은 `product.bikeSpecs.*` 메시지
 *
 * 스키마(Bestbike 온보딩):
 * `{ kind: "bike", maker, model, variant, classKey, type, transmission, leadHours }`
 */

import { BIKE_CLASSES, type BikeClassKey } from "./bike-classes";
import { BIKE_TYPE_KEYS, type BikeType } from "./bike-facets";
import { defaultLocale, locales, type Locale } from "@/i18n/routing";

/** 표시 순서 = 아래 배열 순서 (제조사 → 차종 → 클래스 → 변속 → 타입 → 예약 마감). */
export type BikeSpecRowKey =
  | "maker"
  | "model"
  | "class"
  | "transmission"
  | "type"
  | "leadTime";

export interface BikeSpecRow {
  key: BikeSpecRowKey;
  label: string;
  value: string;
}

/** `useTranslations("product.bikeSpecs")` 가 돌려주는 함수의 최소 형태. */
export type BikeSpecTranslator = (
  key: string,
  values?: Record<string, string | number>,
) => string;

/** 타입 키 집합의 정본은 `bike-facets.ts`(허브 필터 축과 공유). 여기서는 재노출만. */
export { BIKE_TYPE_KEYS } from "./bike-facets";
export type BikeTypeKey = BikeType;

const BIKE_TYPE_KEY_SET = new Set<string>(BIKE_TYPE_KEYS);

/**
 * 색상 원문(파트너 데이터는 일본어 한 글자) → 메시지 키.
 * 사전에 없는 값은 번역하지 않고 원문 그대로 둔다(지어내는 것보다 안전).
 */
const VARIANT_COLOR_KEYS: Readonly<Record<string, string>> = {
  黒: "black",
  白: "white",
  銀: "silver",
  黄: "yellow",
  赤: "red",
  青: "blue",
};

const BIKE_CLASS_LABELS = new Map<string, Record<Locale, string>>(
  BIKE_CLASSES.map((c) => [c.key as BikeClassKey, c.label]),
);

/** `specifications` jsonb 중 이 화면이 읽는 필드만 추린 형태. */
export interface BikeSpecifications {
  kind: "bike";
  maker?: string | null;
  model?: string | null;
  variant?: string | null;
  classKey?: string | null;
  type?: string | null;
  transmission?: string | null;
  leadHours?: number | null;
  equipment?: Partial<Record<BikeEquipmentKey, BikeEquipmentStatus>>;
}

export type BikeEquipmentKey = "smartphoneHolder" | "usbCharger" | "topBox";
export type BikeEquipmentStatus = "provided" | "notProvided" | "varies" | "unverified";

export function getBikeEquipment(
  specs: Record<string, unknown> | null | undefined,
): Record<BikeEquipmentKey, BikeEquipmentStatus> | null {
  if (!isBikeSpecifications(specs) || !specs.equipment || typeof specs.equipment !== "object") return null;
  const equipment = specs.equipment as Record<string, unknown>;
  const valid = new Set<BikeEquipmentStatus>(["provided", "notProvided", "varies", "unverified"]);
  const keys: BikeEquipmentKey[] = ["smartphoneHolder", "usbCharger", "topBox"];
  if (!keys.every((key) => valid.has(equipment[key] as BikeEquipmentStatus))) return null;
  return {
    smartphoneHolder: equipment.smartphoneHolder as BikeEquipmentStatus,
    usbCharger: equipment.usbCharger as BikeEquipmentStatus,
    topBox: equipment.topBox as BikeEquipmentStatus,
  };
}

/** `products.specifications` 가 오토바이 제원인지 — 이 값이 false 면 표를 아예 그리지 않는다. */
export function isBikeSpecifications(
  specs: Record<string, unknown> | null | undefined,
): specs is Record<string, unknown> & BikeSpecifications {
  return !!specs && specs.kind === "bike";
}

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function toLocale(locale: string | undefined): Locale {
  return (locales as readonly string[]).includes(locale ?? "")
    ? (locale as Locale)
    : defaultLocale;
}

/** 차종 = 모델 + 색상(번역). 색상이 없으면 모델만. */
export function formatBikeModel(
  model: string | null | undefined,
  variant: string | null | undefined,
  t: BikeSpecTranslator,
): string | null {
  const modelText = text(model);
  const variantText = text(variant);
  if (!modelText) return null;
  if (!variantText) return modelText;

  const colorKey = VARIANT_COLOR_KEYS[variantText];
  return `${modelText} ${colorKey ? t(`colors.${colorKey}`) : variantText}`;
}

/** 변속 — AT/MT 외의 값은 행을 그리지 않는다. */
export function formatBikeTransmission(
  transmission: string | null | undefined,
  t: BikeSpecTranslator,
): string | null {
  const value = text(transmission)?.toUpperCase();
  if (value === "AT") return t("transmissionAt");
  if (value === "MT") return t("transmissionMt");
  return null;
}

/** 타입 — 사전에 없는 키는 "기타"로 떨어뜨린다(원문 키를 그대로 노출하지 않는다). */
export function formatBikeType(
  type: string | null | undefined,
  t: BikeSpecTranslator,
): string | null {
  const value = text(type);
  if (!value) return null;
  const key: BikeTypeKey = BIKE_TYPE_KEY_SET.has(value)
    ? (value as BikeTypeKey)
    : "other";
  return t(`types.${key}`);
}

/**
 * 제원 표 행 목록. 값이 없는 행은 통째로 빠지며, 남는 행이 하나도 없으면 빈 배열
 * (컴포넌트는 그때 섹션 자체를 그리지 않는다).
 */
export function formatBikeSpecs(
  specs: Record<string, unknown> | null | undefined,
  t: BikeSpecTranslator,
  locale?: string,
  /** 그 언어 차종 이름(모델 허브 이름 + 사양) — 있으면 원문 `specs.model` 대신 쓴다 */
  modelName?: string | null,
): BikeSpecRow[] {
  if (!isBikeSpecifications(specs)) return [];

  const resolved = toLocale(locale);
  const classLabels = BIKE_CLASS_LABELS.get(text(specs.classKey) ?? "");
  const leadHours =
    typeof specs.leadHours === "number" &&
    Number.isFinite(specs.leadHours) &&
    specs.leadHours > 0
      ? specs.leadHours
      : null;

  const values: Array<[BikeSpecRowKey, string | null]> = [
    ["maker", text(specs.maker)],
    ["model", modelName?.trim() || formatBikeModel(specs.model, specs.variant, t)],
    ["class", classLabels ? classLabels[resolved] : null],
    ["transmission", formatBikeTransmission(specs.transmission, t)],
    ["type", formatBikeType(specs.type, t)],
    ["leadTime", leadHours === null ? null : t("leadTimeValue", { hours: leadHours })],
  ];

  return values
    .filter((entry): entry is [BikeSpecRowKey, string] => entry[1] !== null)
    .map(([key, value]) => ({ key, label: t(`labels.${key}`), value }));
}

/**
 * 표에 그릴 행이 하나라도 있는지 — 탭/앵커를 띄울지 판단할 때 쓴다.
 * 번역기 대신 키를 그대로 돌려주는 항등 함수를 넣어 `formatBikeSpecs` 와 같은
 * 판정을 재사용한다(라벨은 절대 비지 않으므로 행 개수는 동일하다).
 */
export function hasBikeSpecs(
  specs: Record<string, unknown> | null | undefined,
): boolean {
  return formatBikeSpecs(specs, (key) => key).length > 0;
}
